/**
 *
 * ListResources
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { withStyles } from '@material-ui/core/styles';
import ButtonBase from '@material-ui/core/ButtonBase';
import CloseIcon from '@material-ui/icons/Close';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import AddCircleIcon from '@material-ui/icons/AddCircleOutline';
import { makeSelectCreateListResult } from 'containers/EditorLists/selectors';
import Rating from 'components/ResourceRating';
import HeartEmptyIcon from 'components/SaveAsFavorite/images/heart-empty.svg';
import HeartIcon from 'components/SaveAsFavorite/images/heart-full.svg';
import { getUserRatingAndCount } from 'components/UserAndExpertRating/utils';

const styles = theme => ({
  paper: {
    position: 'absolute',
    width: '60%',
    maxHeight: '90%',
    overflowY: 'auto',
    boxShadow: theme.shadows[5],
    outline: 'none',
    left: '20%',
    top: '5%',
  },
  header: {
    marginLeft: 32,
    marginRight: 32,
    marginTop: 16,
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  closeIcon: {
    marginRight: -12,
    padding: 6,
  },
  headerText: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  group: {
    marginLeft: 32,
    marginRight: 32,
    marginTop: 16,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row-reverse',
    marginTop: 8,
  },
  newListButton: {
    cursor: 'pointer',
    paddingTop: 4,
    paddingBottom: 4,
    marginTop: 8,
    marginBottom: 8,
  },
  cancelButton: {
    background: '#ffffff',
    color: theme.palette.primary.main,
    padding: '8px 12px',
    marginRight: 8,
    '&:hover': {
      opacity: 0.8,
    },
    ...theme.typography.pSmallBold,
  },
  confirmButton: {
    background: theme.palette.primary.main,
    color: '#ffffff',
    padding: '8px 12px',
    '&:hover': {
      opacity: 0.8,
    },
    ...theme.typography.pSmallBold,
  },
  suggestionWrapper: {
    overflowY: 'auto',
    maxHeight: 132,
    marginTop: 8,
  },
  suggestionHeader: {
    ...theme.typography.pBold,
  },
  suggestionContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTop: 'solid 1px #EBEBEB',
    paddingTop: '12px !important',
    paddingBottom: '12px !important',
    '&:first-child': {
      borderTop: 'none',
    },
  },
  suggestionName: {
    fontSize: 16,
  },
  icon: {
    padding: 0,
    backgroundColor: 'transparent',
    '&:hover': {
      background: 'none',
    },
  },
  heart: {
    width: 20,
  },
  textFieldContainer: {
    '& label': {
      fontSize: '1.063rem',
    },
    '& input': {
      fontSize: '1.063rem',
    },
  },
  resourceInfo: {
    marginTop: 24,
    paddingLeft: 32,
    paddingRight: 32,
    paddingTop: 16,
    paddingBottom: 16,
    boxShadow: '0px -1px 10px 0px #ccc',
  },
  resourceDescription: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    paddingTop: '16px !important',
    paddingBottom: '16px !important',
  },
  resourceRating: {
    display: 'flex',
    alignItems: 'center',
  },
  descriptionBody: {
    marginTop: 8,
    maxHeight: 63,
    display: '-webkit-box',
    '-webkit-line-clamp': '3',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  remainingResource: {
    maxHeight: 21,
    display: '-webkit-box',
    '-webkit-line-clamp': '1',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

class ListResources extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isCreateNewList: _isEmpty(props.lists),
      listName: '',
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { lists, result } = this.props;
    const { isCreateNewList } = this.state;
    if (
      nextProps.result !== result &&
      nextProps.result.added &&
      isCreateNewList
    ) {
      this.setState({ isCreateNewList: false, listName: '' });
    }

    if (lists !== nextProps.lists)
      this.setState({ isCreateNewList: _isEmpty(nextProps.lists) });
  }

  render() {
    const {
      classes,
      data: {
        fields: { resources },
      },
      lists,
      onClose,
      onSubmit,
    } = this.props;
    const { isCreateNewList, listName } = this.state;

    const firstResource = resources[0];
    const type = getContentType(_get(firstResource, 'sys.contentType.sys.id'));
    const { rating, count } = getUserRatingAndCount(firstResource.fields, type);
    const charLimit = { name: 35, description: 150 };
    const resourceName =
      _get(firstResource, 'fields.name') || _get(firstResource, 'fields.title');
    const truncatedName = resourceName.substr(0, charLimit.name);
    const finalResourceName =
      resourceName.length > charLimit.name
        ? `${truncatedName}...`
        : truncatedName;
    const remainingResources = resources.slice(1, 4);

    return (
      <Paper elevation={0} className={classes.paper}>
        <Grid container>
          <Grid item xs={12} className={classes.header}>
            <Typography className={classes.headerText}>Save to List</Typography>
            <IconButton
              onClick={onClose}
              className={classes.closeIcon}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </Grid>
          <Grid item xs={12} className={classes.group}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              onClick={() => this.setState({ isCreateNewList: true })}
              className={classes.newListButton}
            >
              Copy Resources into New List&nbsp;&nbsp;&nbsp;
              {!isCreateNewList && <AddCircleIcon fontSize="small" />}
            </Typography>
            {isCreateNewList && (
              <>
                <TextField
                  variant="outlined"
                  name="list-name"
                  classes={{
                    root: classes.textFieldContainer,
                  }}
                  fullWidth
                  label="Name your list"
                  value={listName}
                  onChange={e => this.setState({ listName: e.target.value })}
                  required
                  autoComplete="off"
                />
                <div className={classes.buttonContainer}>
                  <ButtonBase
                    className={classes.confirmButton}
                    onClick={() => onSubmit({ listName })}
                    disableRipple
                  >
                    Create
                  </ButtonBase>
                  <ButtonBase
                    className={classes.cancelButton}
                    onClick={onClose}
                    disableRipple
                  >
                    Cancel
                  </ButtonBase>
                </div>
              </>
            )}
          </Grid>
          {lists.length > 0 && (
            <Grid item xs={12} className={classes.group}>
              <Typography
                color="textPrimary"
                className={classes.suggestionHeader}
              >
                Or Add Resources into Existing List:
              </Typography>
              <Grid container spacing={2} className={classes.suggestionWrapper}>
                {lists
                  .sort((a, b) => b.resources.length - a.resources.length)
                  .map(el => {
                    const listResourcesSlugs = el.resources.map(
                      resource => resource.slug,
                    );
                    const isSaved = resources.every(resource =>
                      listResourcesSlugs.includes(resource.fields.slug),
                    );

                    return (
                      <Grid
                        item
                        xs={12}
                        className={classes.suggestionContainer}
                      >
                        <Typography className={classes.suggestionName}>
                          {`${el.name} (${el.resources.length})`}
                        </Typography>
                        <IconButton
                          className={classes.icon}
                          onClick={() => onSubmit({ nodeID: el.id, isSaved })}
                          disableRipple
                        >
                          <img
                            src={isSaved ? HeartIcon : HeartEmptyIcon}
                            alt="Full"
                            className={classes.heart}
                          />
                        </IconButton>
                      </Grid>
                    );
                  })}
              </Grid>
            </Grid>
          )}
          <Grid item xs={12} className={classes.resourceInfo}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6">{finalResourceName}</Typography>
                <Typography
                  variant="overline"
                  className={classes.descriptionBody}
                >
                  {documentToPlainTextString(
                    _get(firstResource, 'fields.description', ''),
                  )
                    .replace(/<sup>(.*?)<\/sup>/g, '')
                    .replace(/<sub>(.*?)<\/sub>/g, '')}
                </Typography>
                {type !== 'person' && type !== 'assessments' && (
                  <div className={classes.resourceRating}>
                    <Rating value={rating} total={count} isEditable={false} />
                  </div>
                )}
              </Grid>
              <Grid item xs={12} sm={6} className={classes.resourceDescription}>
                {remainingResources.map(resource => {
                  const name =
                    _get(resource, 'fields.name') ||
                    _get(resource, 'fields.title');
                  return (
                    <Typography
                      variant="caption"
                      className={classes.remainingResource}
                    >
                      {name}
                    </Typography>
                  );
                })}
                {resources.length > 4 && (
                  <Typography variant="overline">
                    ...and {resources.length - 4} more
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    );
  }
}

function getContentType(type) {
  switch (type) {
    case 'application':
      return 'app';
    case 'activity':
      return 'read';
    default:
      return type;
  }
}

ListResources.propTypes = {
  classes: PropTypes.object,
  data: PropTypes.array,
  lists: PropTypes.array,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  result: PropTypes.object,
};

const mapStateToProps = createStructuredSelector({
  result: makeSelectCreateListResult(),
});

const withConnect = connect(mapStateToProps);

export default compose(
  withStyles(styles),
  withConnect,
)(ListResources);
